// Rotating announcement messages with previous/next arrows. The outgoing message moves up 8px and fades out, then
// the next one comes in the same way (150ms each). Rotation pauses while the bar is hovered or has keyboard focus,
// and doesn't run at all for visitors who ask for reduced motion. Screen readers hear a message only when the
// visitor changes it with the arrows, not every few seconds.
class AnnouncementBar extends HTMLElement {
  #index = 0;
  #timer;
  #busy = false;

  connectedCallback() {
    this.messages = [...this.querySelectorAll('.pl-announcement__message')];
    this.live = this.querySelector('[data-messages]');
    this.addEventListener('click', (event) => {
      const arrow = event.target.closest('[data-step]');
      if (!arrow) return;
      this.live?.setAttribute('aria-live', 'polite');
      this.go(this.#index + Number(arrow.dataset.step));
    });
    for (const type of ['mouseenter', 'focusin']) this.addEventListener(type, () => this.#pause(true));
    for (const type of ['mouseleave', 'focusout']) this.addEventListener(type, () => this.#pause(false));
    this.addEventListener('shopify:block:select', (event) => this.go(this.messages.indexOf(event.target), false));
    this.#start();
  }

  disconnectedCallback() {
    clearInterval(this.#timer);
  }

  #paused = false;

  #pause(paused) {
    this.#paused = paused;
    clearInterval(this.#timer);
    if (!paused) this.#start();
  }

  #start() {
    const seconds = Number(this.dataset.cycleSeconds);
    if (!seconds || this.#paused || matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    clearInterval(this.#timer);
    this.#timer = setInterval(() => this.go(this.#index + 1), seconds * 1000);
  }

  async go(index, animate = true) {
    const next = (index + this.messages.length) % this.messages.length;
    if (next === this.#index || this.#busy) return;
    this.#busy = true;
    clearInterval(this.#timer);
    const duration = animate && !matchMedia('(prefers-reduced-motion: reduce)').matches ? 150 : 0;
    const options = { duration, easing: 'ease-in-out' };
    const current = this.messages[this.#index];
    await current.animate({ transform: ['translateY(0)', 'translateY(-8px)'], opacity: [1, 0] }, options).finished;
    current.hidden = true;
    const incoming = this.messages[next];
    incoming.hidden = false;
    await incoming.animate({ transform: ['translateY(8px)', 'translateY(0)'], opacity: [0, 1] }, options).finished;
    this.#index = next;
    this.#busy = false;
    this.#start();
  }
}

if (!customElements.get('pl-announcement-bar')) customElements.define('pl-announcement-bar', AnnouncementBar);
